/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { Observable } from "@vienna-hypertext/observable";
import { v4 as uuid } from "uuid";

import { JobStatus, LogType, Message, MessageRole } from "@vienna/common";

import { ExampleData } from "src/classes/plate/Generator/example-data";
import { Log } from "src/classes/plate/Generator/Logger/Log";

export type LoggerSnapshot = {
  logs: Log[];
  pendingLogId: string | null;
  totalTokens: number;
  dataFeed: string | null;
  thinking: boolean;
};
export class Logger extends Observable<LoggerSnapshot> {
  private startThinkingTimer: ReturnType<typeof setTimeout> | null = null;

  private startThinkingAfter: number = 1000;

  private constructor(snapshot: LoggerSnapshot) {
    super(snapshot);
  }

  /* -------------------------------------------------- */
  /*                      Tokens                        */
  /* -------------------------------------------------- */
  public logDataToken(token: string): void {
    this.incrementTokens();
    this.setState({ dataFeed: this.snapshot.dataFeed + token });
  }

  public handleIncomingToken(token: string, logId: string, logType: LogType): void {
    this.incrementTokens();
    this.stopThinking();
    this.scheduleThinking();
    const existingLog = this.snapshot.logs.find((log) => log.snapshot.id === logId);
    if (!existingLog) {
      const log = Log.create(MessageRole.AI, logType, token, logId);
      this.addLog(log);
    } else {
      existingLog.addContent(token);
    }
  }

  /* -------------------------------------------------- */
  /*                    One-Off Logs                    */
  /* -------------------------------------------------- */
  public addUserMessage(content: string): void {
    const log = Log.create(MessageRole.Human, LogType.Message, content, uuid());
    this.addLog(log);
  }

  public onJobStart(): void {
    this.startThinking();
  }

  public onJobEnd(res: { status: JobStatus; errorMessage?: string | null }, shouldLog: boolean): void {
    if (shouldLog) {
      switch (res.status) {
        case JobStatus.Success:
          this.addSystemLog("Generation completed", LogType.Success);
          break;
        case JobStatus.Canceled:
          this.addSystemLog("Generation canceled", LogType.Info);
          break;
        case JobStatus.Failed:
          const message = res.errorMessage || "Something went wrong";
          this.addSystemLog(`Generation error: ${message}`, LogType.Failure);
          break;
      }
    }
    this.stopThinking();
    this.setState({ pendingLogId: null, logs: [] });
  }

  private addSystemLog(content: string, type: LogType): void {
    const log = Log.create(MessageRole.System, type, content, uuid());
    this.addLog(log);
  }

  public requestConfirmation(logId: string): void {
    this.setState({ pendingLogId: logId });
    this.stopThinking();
  }

  public handleConfirmation(): void {
    this.setState({ pendingLogId: null });
    this.scheduleThinking();
  }

  /* -------------------------------------------------- */
  /*                       State                        */
  /* -------------------------------------------------- */
  private addLog(log: Log): void {
    this.setState({ logs: [...this.snapshot.logs, log] });
    this.scheduleThinking();
  }

  private incrementTokens(): void {
    this.setState({ totalTokens: this.snapshot.totalTokens + 1 });
  }

  /* ------------------— Thinking... —------------------ */

  private scheduleThinking(): void {
    // clear existing timeout if there is one
    if (this.startThinkingTimer) {
      clearTimeout(this.startThinkingTimer);
    }

    // set a new timeout, which should really be called a timer
    this.startThinkingTimer = setTimeout(() => {
      this.startThinking();
    }, this.startThinkingAfter);
  }

  public startThinking(): void {
    this.setState({ thinking: true });
  }

  public stopThinking(): void {
    this.setState({ thinking: false });
    if (this.startThinkingTimer) {
      clearTimeout(this.startThinkingTimer);
    }
  }

  /* -------------------------------------------------- */
  /*                       Utils                        */
  /* -------------------------------------------------- */
  public get messages(): Message[] {
    const messageLogs = this.snapshot.logs.filter((log) => log.snapshot.type === LogType.Message);
    const messages = messageLogs.map((log) => ({ text: log.snapshot.content, role: log.snapshot.role }));
    return messages;
  }

  /* -------------------------------------------------- */
  /*                       Create                       */
  /* -------------------------------------------------- */
  public static create(): Logger {
    const initialSnapshot: LoggerSnapshot = {
      logs: [],
      pendingLogId: null,
      totalTokens: 0,
      dataFeed: null,
      thinking: false,
    };
    return new Logger(initialSnapshot);
  }

  public static example(): Logger {
    const initialSnapshot: LoggerSnapshot = {
      logs: ExampleData.logSnapshots.map((snapshot) => Log.fromSnapshot(snapshot)),
      pendingLogId: "05bbf165-ee4f-4678-aec3-f9dbfd7bb47e",
      totalTokens: 0,
      dataFeed: null,
      thinking: false,
    };
    return new Logger(initialSnapshot);
  }

  /* -------------------------------------------------- */
  /*                       Destroy                      */
  /* -------------------------------------------------- */
  public destroy(): void {
    this.stopThinking();
  }
}
